import React from "react";
import PropTypes from "prop-types";
import {
    NeoCard,
    NeoInput,
    NeoButton
} from "../../../components/Common";
import { MEMBER_STATUS_OPTIONS } from "../../../constants/members";
import { GENDER_OPTIONS } from "../../../constants/gender";
import { DURATION_OPTIONS } from "../constants";

/**
 * CrmFilterBar
 * Painel lateral de filtros do módulo CRM de vendas.
 * Utiliza o Neo Design System (NeoCard, NeoInput, NeoButton) de alta fidelidade
 * e elimina 100% dos estilos inline no JSX, prendendo a rolagem estritamente dentro do container.
 */
const CrmFilterBar = ({
    filters,
    activeFilters,
    updateActiveFilter,
    updateFilter,
    resetFilters,
    onSearch,
    memberships,
    activities,
    employees,
    loading,
    loadingRefs,
}) => {
    const allEmployees = employees || [];

    const handleKeyDown = (e) => {
        if (e.key === "Enter") onSearch();
    };

    return (
        <NeoCard
            className="neo-sticky-sidebar"
            title="CRM - Filtros"
            subtitle="Segmentação de clientes"
            headerIcon="mdi-filter-variant"
            bodyClassName="d-flex flex-column p-0 overflow-hidden"
        >
            {/* FILTROS SCROLLÁVEIS */}
            <div className="flex-grow-1 overflow-auto custom-scrollbar p-4 d-flex flex-column gap-3">

                {/* Busca Rápida */}
                <FilterField label="Busca Rápida" icon="mdi-magnify">
                    <NeoInput
                        type="text"
                        placeholder="Nome, Email, CPF ou Telefone..."
                        value={filters.search}
                        onChange={e => updateFilter("search", e.target.value)}
                        onKeyDown={handleKeyDown}
                        icon="mdi-magnify"
                    />
                </FilterField>

                {/* Status */}
                <FilterField label="Status" icon="mdi-account-check-outline" filterKey="status" isActive={activeFilters?.status} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.status}
                        onChange={e => updateFilter("status", e.target.value)}
                        options={[
                            { value: "", label: "Todos" },
                            ...MEMBER_STATUS_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))
                        ]}
                    />
                </FilterField>

                {/* Sexo */}
                <FilterField label="Sexo" icon="mdi-gender-male-female" filterKey="gender" isActive={activeFilters?.gender} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.gender}
                        onChange={e => updateFilter("gender", e.target.value)}
                        options={[
                            { value: "", label: "Todos" },
                            ...GENDER_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))
                        ]}
                    />
                </FilterField>

                {/* Contrato / Plano */}
                <FilterField label="Contrato / Plano" icon="mdi-file-document-outline" filterKey="membershipId" isActive={activeFilters?.membershipId} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.membershipId}
                        onChange={e => updateFilter("membershipId", e.target.value)}
                        disabled={loadingRefs}
                        options={[
                            { value: "", label: "Qualquer Contrato" },
                            ...(memberships || []).map(m => ({ value: m.id, label: m.name }))
                        ]}
                    />
                </FilterField>

                {/* Duração do Plano */}
                <FilterField label="Duração do Plano" icon="mdi-clock-outline" filterKey="durationType" isActive={activeFilters?.durationType} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.durationType}
                        onChange={e => updateFilter("durationType", e.target.value)}
                        options={DURATION_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                    />
                </FilterField>

                {/* Vencimento do Contrato */}
                <FilterField label="Vencimento do Contrato" icon="mdi-calendar-range" filterKey="contractDate" isActive={activeFilters?.contractDate} onToggle={updateActiveFilter}>
                    <div className="d-flex gap-2">
                        <NeoInput
                            type="date"
                            value={filters.contractStartDate}
                            onChange={e => updateFilter("contractStartDate", e.target.value)}
                        />
                        <NeoInput
                            type="date"
                            value={filters.contractEndDate}
                            onChange={e => updateFilter("contractEndDate", e.target.value)}
                        />
                    </div>
                </FilterField>

                {/* Atividade / Modalidade */}
                <FilterField label="Atividade / Modalidade" icon="mdi-dumbbell" filterKey="activityId" isActive={activeFilters?.activityId} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.activityId}
                        onChange={e => updateFilter("activityId", e.target.value)}
                        disabled={loadingRefs}
                        options={[
                            { value: "", label: "Todas" },
                            ...(activities || []).map(a => ({ value: a.id, label: a.name }))
                        ]}
                    />
                </FilterField>

                {/* Faixa Etária */}
                <FilterField label="Faixa Etária" icon="mdi-cake-variant-outline" filterKey="age" isActive={activeFilters?.age} onToggle={updateActiveFilter}>
                    <div className="d-flex gap-2">
                        <NeoInput
                            type="number"
                            placeholder="Min"
                            value={filters.ageMin}
                            onChange={e => updateFilter("ageMin", e.target.value)}
                            min={0}
                            max={120}
                        />
                        <NeoInput
                            type="number"
                            placeholder="Max"
                            value={filters.ageMax}
                            onChange={e => updateFilter("ageMax", e.target.value)}
                            min={0}
                            max={120}
                        />
                    </div>
                </FilterField>

                {/* Professor */}
                <FilterField label="Professor" icon="mdi-human-male-board" filterKey="employeeId" isActive={activeFilters?.employeeId} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.employeeId}
                        onChange={e => updateFilter("employeeId", e.target.value)}
                        disabled={loadingRefs}
                        options={[
                            { value: "", label: "Todos" },
                            ...allEmployees.map(e => ({
                                value: e.id,
                                label: e.fullName || e.name || `${e.firstName || ""} ${e.lastName || ""}`.trim()
                            }))
                        ]}
                    />
                </FilterField>

                {/* Consultor (Venda) */}
                <FilterField label="Consultor (Venda)" icon="mdi-account-tie-outline" filterKey="consultantId" isActive={activeFilters?.consultantId} onToggle={updateActiveFilter}>
                    <NeoInput
                        type="select"
                        value={filters.consultantId}
                        onChange={e => updateFilter("consultantId", e.target.value)}
                        disabled={loadingRefs}
                        options={[
                            { value: "", label: "Todos" },
                            ...allEmployees.map(e => ({
                                value: e.id,
                                label: e.fullName || e.name || `${e.firstName || ""} ${e.lastName || ""}`.trim()
                            }))
                        ]}
                    />
                </FilterField>

                {/* Data da Venda (Conversão) */}
                <FilterField label="Data da Venda (Conversão)" icon="mdi-cart-check" filterKey="saleDate" isActive={activeFilters?.saleDate} onToggle={updateActiveFilter}>
                    <div className="d-flex gap-2">
                        <NeoInput
                            type="date"
                            value={filters.saleStartDate}
                            onChange={e => updateFilter("saleStartDate", e.target.value)}
                        />
                        <NeoInput
                            type="date"
                            value={filters.saleEndDate}
                            onChange={e => updateFilter("saleEndDate", e.target.value)}
                        />
                    </div>
                </FilterField>
            </div>

            {/* FOOTER: BOTÕES DE AÇÃO */}
            <div className="border-top px-4 py-3 bg-white flex-shrink-0 d-flex gap-2">
                <NeoButton
                    variant="primary"
                    className="flex-grow-1"
                    onClick={onSearch}
                    loading={loading}
                    icon="mdi-magnify"
                >
                    Buscar
                </NeoButton>
                <NeoButton
                    variant="outline-chocolate"
                    className="px-3"
                    onClick={resetFilters}
                    title="Limpar filtros"
                    icon="mdi-filter-remove-outline"
                />
            </div>
        </NeoCard>
    );
};

CrmFilterBar.propTypes = {
    filters: PropTypes.object.isRequired,
    activeFilters: PropTypes.object,
    updateActiveFilter: PropTypes.func.isRequired,
    updateFilter: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    memberships: PropTypes.array,
    activities: PropTypes.array,
    employees: PropTypes.array,
    loading: PropTypes.bool,
    loadingRefs: PropTypes.bool,
};

/**
 * FilterField — Wrapper padronizado para cada campo de filtro.
 */
const FilterField = ({ label, icon, filterKey, isActive, onToggle, children }) => (
    <div>
        <label
            className={`neo-filter-field-label ${filterKey ? "neo-filter-field-label-clickable" : ""}`}
            onClick={(e) => {
                if (e.target.type !== "checkbox" && filterKey && onToggle) {
                    onToggle(filterKey, !isActive);
                }
            }}
        >
            {filterKey && (
                <div className="form-check m-0 p-0 d-flex align-items-center">
                    <input
                        type="checkbox"
                        className="form-check-input m-0 neo-filter-field-checkbox"
                        checked={!!isActive}
                        onChange={(e) => onToggle && onToggle(filterKey, e.target.checked)}
                    />
                </div>
            )}
            <div className="d-flex align-items-center">
                <i className={`mdi ${icon} me-1`}></i>
                {label}
            </div>
        </label>
        <div className={`neo-filter-field-content ${(!filterKey || isActive) ? "neo-filter-field-content-active" : "neo-filter-field-content-inactive"}`}>
            {children}
        </div>
    </div>
);

FilterField.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    filterKey: PropTypes.string,
    isActive: PropTypes.bool,
    onToggle: PropTypes.func,
    children: PropTypes.node.isRequired
};

export default CrmFilterBar;
